'use strict';

/**
 * Quick view: fetch section HTML via `?sections=product-card-quickview`,
 * extract `[data-product-card-quickview-html]`, and inject into the dialog.
 */

(() => {
  const SELECTORS = {
    quickviewDialogHost: 'quickview-dialog',
    dialog: '[data-qv-dialog]',
    content: '[data-qv-content]',
    close: '[data-qv-close]',
    popupCloseButton: '[data-qv-popup-close]',
    drawerCloseButton: '[data-qv-drawer-close]',
    mainSlider: '[data-qv-main-slider]',
    thumbSlider: '[data-qv-thumb-slider]',
    descriptionToggle: '[data-qv-desc-toggle]',
    descriptionContainer: '[data-qv-desc-container]',
  };

  const INJECT_ROOT = '[data-product-card-quickview-html]';
  const SECTION_ID = 'product-card-quickview';
  const CACHE_KEY = 'quickviewDialogHtml';

  function thumbSlideEls(thumbContainer) {
    if (!thumbContainer) return [];

    const wrap = thumbContainer.querySelector('.swiper-wrapper');
    const root = wrap || thumbContainer;

    return Array.from(root.querySelectorAll('.swiper-slide'));
  }

  const MOBILE_QV = window.matchMedia('(max-width: 1024px)');

  const MOBILE_QV_INTERACTIVE = 'quickview-button, button, input, select, textarea, product-form-component, wishlist-button, notify-me, .product-card__atc, .product-card__form';

  document.addEventListener(
    'click',
    (e) => {
      if (!MOBILE_QV.matches) return;

      const target = e.target;
      if (!(target instanceof Element)) return;
      if (target.closest(MOBILE_QV_INTERACTIVE)) return;

      const card = target.closest('article.product-card');
      if (!card || !card.closest('product-card, .product-item')) return;

      const overlay = target.closest('.product-card__link--overlay');
      const media = target.closest('.product-card__media');
      if (!overlay && !media) return;

      const btn = card.querySelector('quickview-button button');
      if (!(btn instanceof HTMLButtonElement) || btn.disabled) return;

      e.preventDefault();
      btn.click();
    },
    true
  );

  class QuickviewButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;

      this.handle = this.button.getAttribute('data-product-handle');
      this.variantId = this.button.getAttribute('data-variant-id');
      this.quickviewType = this.button.getAttribute('data-quickview-type');
      if (!this.handle) return;

      this.onPrefetch = () => this.prefetch();
      this.onOpen = (e) => this.open(e);

      this.button.addEventListener('mouseenter', this.onPrefetch);
      this.button.addEventListener('pointerdown', this.onPrefetch, { passive: true });
      this.button.addEventListener('click', this.onOpen);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              this.prefetch();
              this.observer?.disconnect();
              this.observer = undefined;
              break;
            }
          },
          { rootMargin: '120px 0px' }
        );
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.observer = undefined;

      this.button?.removeEventListener('mouseenter', this.onPrefetch);
      this.button?.removeEventListener('pointerdown', this.onPrefetch);
      this.button?.removeEventListener('click', this.onOpen);
    }

    static async fetchQuickviewHtml(handle, variantId) {
      const cacheKey = variantId ? `${handle}-${variantId}` : handle;
      const cached = QuickviewButton.htmlCache().get(cacheKey);
      if (cached) return cached;

      let sectionUrl = `/products/${handle}?sections=${SECTION_ID}`;
      if (variantId) {
        sectionUrl += `&variant=${variantId}`;
      }

      const res = await fetch(sectionUrl);
      if (!res.ok) throw new Error('Quickview fetch failed');

      const data = await res.json();
      const sectionHtml = data?.[SECTION_ID];

      if (typeof sectionHtml !== 'string') {
        throw new Error('Quickview section response missing HTML');
      }

      QuickviewButton.htmlCache().set(cacheKey, sectionHtml);
      return sectionHtml;
    }

    async prefetch() {
      const cacheKey = this.variantId ? `${this.handle}-${this.variantId}` : this.handle;
      if (QuickviewButton.htmlCache().has(cacheKey)) return;
      try {
        await QuickviewButton.fetchQuickviewHtml(this.handle, this.variantId);
      } catch {
        /* ignore */
      }
    }

    async open(e) {
      e.preventDefault();

      const quickviewDialog = document.querySelector(SELECTORS.quickviewDialogHost);
      if (!quickviewDialog || typeof quickviewDialog.openDialog !== 'function') {
        console.error('Quickview: dialog component not found.');
        return;
      }

      this.setLoading(true);

      try {
        const html = await QuickviewButton.fetchQuickviewHtml(this.handle, this.variantId);
        const root = QuickviewButton.parseInjectRoot(html);

        if (!root) {
          window._theme?.Helpers?.showNotification?.('Quick view is not available.', 4000);
          return;
        }

        quickviewDialog.openDialog(root.innerHTML, this.button, this.quickviewType);
      } catch (err) {
        console.error(err);
        window._theme?.Helpers?.showNotification?.(
          'Unable to load this product. Please try again.',
          4000
        );
      } finally {
        this.setLoading(false);
      }
    }

    static parseInjectRoot(html) {
      if (!html || typeof html !== 'string') return null;

      const template = document.createElement('template');
      template.innerHTML = html.trim();
      return template.content.querySelector(INJECT_ROOT);
    }

    static htmlCache() {
      return window[CACHE_KEY] || (window[CACHE_KEY] = new Map());
    }

    setLoading(state) {
      if (!this.button) return;

      this.button.toggleAttribute('disabled', state);
      this.button.toggleAttribute('aria-busy', state);
      this.classList.toggle('is-loading', state);
    }
  }
  if (!customElements.get('quickview-button')) {
    customElements.define('quickview-button', QuickviewButton);
  }

  class QuickviewDialog extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector(SELECTORS.dialog);
      this.content = this.querySelector(SELECTORS.content);
      this.popupCloseButton = this.querySelector(SELECTORS.popupCloseButton);
      this.drawerCloseButton = this.querySelector(SELECTORS.drawerCloseButton);
      if (!(this.dialog instanceof HTMLDialogElement)) return;

      this.onClick = (e) => {
        const descriptionToggle = e.target.closest(SELECTORS.descriptionToggle);
        if (descriptionToggle) {
          this.toggleDescription(descriptionToggle);
          return;
        }

        if (e.target.closest(SELECTORS.close)) this.closeDialog();
      };
      this.onKeydown = (e) => {
        if (!this.hasAttribute('open')) return;
        if (e.key === 'Escape') this.closeDialog();
        if (e.key === 'Tab') this.trapTabFocus(e);
      };
      this.onNativeClose = () => this.closeDialog(true);

      this.onVariantChanged = (e) => {
        if (!this.hasAttribute('open')) return;
        this.handlePriceAnnouncement();
      };

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      this.dialog.addEventListener('close', this.onNativeClose);
      document.addEventListener('variant:changed', this.onVariantChanged);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      this.dialog?.removeEventListener('close', this.onNativeClose);
      document.removeEventListener('variant:changed', this.onVariantChanged);
    }

    openDialog(innerHtml, triggerBtn, quickviewType = 'popup') {
      this.triggerButton = triggerBtn;

      if (!(this.dialog instanceof HTMLDialogElement)) return;
      if (!this.content) {
        this.content = this.querySelector(SELECTORS.content);
      }
      if (!this.content) {
        console.error('Quick view: [data-qv-content] missing.');
        return;
      }

      if (this.dialog.open) {
        this.dialog.close();
      }

      if (this.hasAttribute('open') && !this.dialog.open) {
        this.removeAttribute('open');
        this.classList.remove('drawer', 'popup');
        document.body.classList.remove('quickViewDialog');
      }

      this.setAttribute('open', '');
      this.classList.remove('drawer', 'popup');

      if (quickviewType === 'drawer') {
        this.classList.add('drawer');
      } else if (quickviewType === 'popup') {
        this.classList.add('popup');
      }

      this.content.innerHTML = innerHtml;

      const elementsToHide = this.content.querySelectorAll(
        '.pdp-labels, .bestseller-tag, .disc-text-qv, .offer-tag-style'
      );
      elementsToHide.forEach((el) => {
        el.setAttribute('aria-hidden', 'true');
      });

      // Remove role='status' and aria-live from the visual price sections to prevent double announcements
      const priceContainers = this.content.querySelectorAll('[data-product-price-root], .priceSection');
      priceContainers.forEach((el) => {
        el.removeAttribute('role');
        el.removeAttribute('aria-live');
      });

      this._isInitialVariantChange = true;



      // Relocate or create price live region inside the dialog
      let priceLive = document.getElementById('price-aria-live');
      if (!priceLive) {
        priceLive = document.createElement('div');
        priceLive.id = 'price-aria-live';
        priceLive.setAttribute('aria-live', 'polite');
        priceLive.setAttribute('aria-atomic', 'true');
        Object.assign(priceLive.style, {
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: '0',
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: '0',
        });
      }
      this.dialog.appendChild(priceLive);

      if (!this.dialog.open) this.dialog.showModal();
      document.body.classList.add('quickViewDialog');

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.initSliders();
          // ADA requirement: always move focus to the visible close button first.
          const initialFocus = this.setInitialFocus();
          if (initialFocus) {
            initialFocus.focus();
          } else {
            // Fallback: focus the native dialog element itself.
            this.dialog.focus();
          }
        });
      });
    }

    closeDialog(event = false) {
      if (!(this.dialog instanceof HTMLDialogElement)) return;

      if (!event) {
        if (!this.hasAttribute('open') && !this.dialog.open) return;
        if (this.dialog.open) {
          this.dialog.close(); // Call dialog element's native .close() method
          return;
        }
      }

      this.removeAttribute('open');
      this.classList.remove('drawer', 'popup');
      document.body.classList.remove('quickViewDialog');



      // Move price live region back to document.body if present
      const priceLive = this.dialog.querySelector('#price-aria-live');
      if (priceLive) {
        document.body.appendChild(priceLive);
      }

      if (this.content) this.content.innerHTML = '';
      if (this.triggerButton && document.contains(this.triggerButton)) {
        this.triggerButton.focus();
      }
    }



    announcePrice(message) {
      const ariaLive = this.dialog.querySelector('#price-aria-live');
      if (!ariaLive) return;

      clearTimeout(this._priceAnnounceTimer);
      ariaLive.textContent = '';

      setTimeout(() => {
        ariaLive.textContent = message;
        this._priceAnnounceTimer = setTimeout(() => {
          ariaLive.textContent = '';
        }, 3000);
      }, 50);
    }

    handlePriceAnnouncement() {
      if (this._isInitialVariantChange) {
        this._isInitialVariantChange = false;
        return;
      }

      // Wait for DOM updates to complete
      setTimeout(() => {
        const priceSec = this.content.querySelector('.quickview-dialog__price-section, .priceSection');
        if (!priceSec) return;

        // Clean up the text by replacing multiple spaces/newlines with a single space
        const text = priceSec.textContent.replace(/\s+/g, ' ').trim();
        if (text) {
          this.announcePrice(text);
        }
      }, 100);
    }

    initSliders() {
      const main = this.querySelector(SELECTORS.mainSlider);
      const thumb = this.querySelector(SELECTORS.thumbSlider);
      if (!main || !thumb) return;

      const bind = (mainSwiper, thumbSwiper) => {
        mainSwiper.on('slideChange', () => {
          thumbSwiper.slideTo(mainSwiper.activeIndex);
          this.updateThumbs(thumb, mainSwiper.activeIndex);
          this.fixPaginationBulletsAccessibility(main);
        });
        thumbSlideEls(thumb).forEach((slide, i) => {
          slide.addEventListener('click', () => mainSwiper.slideTo(i));
        });
        this.updateThumbs(thumb, 0);
        this.fixPaginationBulletsAccessibility(main);
      };

      const onThumbReady = (mainSwiper) => (thumbEvent) => {
        bind(mainSwiper, thumbEvent.detail.swiper);
      };

      const onMainInit = (e) => {
        const mainSwiper = e.detail.swiper;
        if (thumb.getAttribute('data-initialized') === 'true' && thumb.swiper) {
          bind(mainSwiper, thumb.swiper);
        } else {
          thumb.addEventListener('swiper:init', onThumbReady(mainSwiper));
        }
      };

      if (main.getAttribute('data-initialized') === 'true' && main.swiper) {
        onMainInit({ detail: { swiper: main.swiper } });
      } else {
        main.addEventListener('swiper:init', onMainInit);
      }
    }

    updateThumbs(container, index) {
      thumbSlideEls(container).forEach((el, i) => {
        el.classList.toggle('is-active', i === index);

        const button = el.querySelector('button') || (el.matches('button') ? el : null);
        if (button) {
          if (!button.hasAttribute('role')) {
            button.setAttribute('role', 'button');
          }
          const productTitle =
            this.querySelector('.product__title, h1, h2')?.textContent?.trim() || '';
          if (
            !button.getAttribute('aria-label') ||
            button.getAttribute('aria-label').trim() === ''
          ) {
            button.setAttribute(
              'aria-label',
              `Load slide ${i + 1}${productTitle ? ' of ' + productTitle : ''}`
            );
          }
          button.setAttribute('aria-current', i === index ? 'true' : 'false');
        }
      });
    }

    fixPaginationBulletsAccessibility(container) {
      if (!container) return;
      const pagination = container.querySelector('.swiper-pagination');
      if (!pagination) return;
      const bullets = pagination.querySelectorAll('.swiper-pagination-bullet');
      const productTitle = this.querySelector('.product__title, h1, h2')?.textContent?.trim() || '';
      bullets.forEach((bullet, idx) => {
        if (!bullet.hasAttribute('role')) {
          bullet.setAttribute('role', 'button');
        }
        if (!bullet.getAttribute('aria-label') || bullet.getAttribute('aria-label').trim() === '') {
          bullet.setAttribute(
            'aria-label',
            `Go to slide ${idx + 1}${productTitle ? ' of ' + productTitle : ''}`
          );
        }
        const isActive = bullet.classList.contains('swiper-pagination-bullet-active');
        bullet.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    toggleDescription(toggleButton) {
      const descriptionContainer = this.querySelector(SELECTORS.descriptionContainer);
      if (!descriptionContainer) return;

      const isExpanded = descriptionContainer.classList.contains('expanded');
      const nextExpanded = !isExpanded;

      descriptionContainer.classList.toggle('expanded', nextExpanded);
      toggleButton.textContent = nextExpanded ? 'Read Less' : 'Read More';
    }

    setInitialFocus() {
      if (this.classList.contains('drawer') && this.drawerCloseButton) {
        return this.drawerCloseButton;
      }

      if (this.popupCloseButton) {
        return this.popupCloseButton;
      }

      return this.getFocusableElements()[0] || null;
    }

    getFocusableElements() {
      return Array.from(
        this.querySelectorAll(
          'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => {
        // if (el.closest('[data-qv-klarna]')) return false;
        if (el.disabled) return false;
        if (el.getAttribute('tabindex') === '-1') return false;
        if (el.getAttribute('aria-hidden') === 'true') return false;
        const style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden') return false;
        return el.getClientRects().length > 0;
      });
    }

    trapTabFocus(e) {
      const focusables = this.getFocusableElements();
      if (!focusables.length) {
        e.preventDefault();
        this.dialog?.focus();
        return;
      }

      const active = document.activeElement;
      const focusInsideDialog =
        active === this.dialog || (active instanceof Node && this.contains(active));

      if (!focusInsideDialog) {
        e.preventDefault();
        (e.shiftKey ? focusables[focusables.length - 1] : focusables[0]).focus();
        return;
      }

      const activeIndex = focusables.indexOf(active);

      // Klarna uses shadow DOM / iframes — focus is inside the dialog but not in our list.
      if (activeIndex === -1) {
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey) {
        if (activeIndex <= 0) {
          e.preventDefault();
          last.focus();
        }
      } else if (activeIndex >= focusables.length - 1) {
        e.preventDefault();
        first.focus();
      }
    }
  }
  if (!customElements.get('quickview-dialog')) {
    customElements.define('quickview-dialog', QuickviewDialog);
  }
})();
